import React, { useState, useEffect } from 'react'
import {
  Input,
  Select,
  SelectContent,
  SelectTrigger,
  SelectItem,
  Icon,
  IconButton,
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '@shopflo/ui'
import { ArrowUUpLeft, CaretDown, CurrencyInr, Percent } from '@phosphor-icons/react'
import {
  AmountType,
  DiscountFieldPaths,
  CouponType,
  AmountEntitlement,
  ShippingEntitlement,
  DiscountEntitlement
} from 'src/lib/types/discountsV2'
import { useDiscountForm } from '../hooks/useDiscountForm'
import { classNames } from 'src/lib/utils/helpers'
import { useLocale } from 'src/lib/hooks/useLocal'
import FormErrorText from 'src/components/common/FormErrorText'

interface DiscountPricingInputProps {
  error?: string
  showUpto?: boolean
}

const DiscountPricingInput = ({ error, showUpto = true }: DiscountPricingInputProps) => {
  const {
    state: { entitlement },
    actions: { setFormState }
  } = useDiscountForm()
  const { t } = useLocale()

  const isPricingEntitlement = entitlement.type === CouponType.AMOUNT || entitlement.type === CouponType.SHIPPING
  const pricingEntitlement = isPricingEntitlement ? (entitlement as AmountEntitlement | ShippingEntitlement) : null

  const [isMaxAmountInputOpen, setIsMaxAmountInputOpen] = useState(!!pricingEntitlement?.maximumConcessionAmount)

  useEffect(() => {
    if (pricingEntitlement?.maximumConcessionAmount) {
      setIsMaxAmountInputOpen(true)
    }
  }, [pricingEntitlement?.maximumConcessionAmount])

  if (!pricingEntitlement) {
    return null
  }

  const { concessionAmount, amountType, maximumConcessionAmount } = pricingEntitlement

  const handleMainAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseFloat(e.target.value) || 0
    if (amountType === AmountType.PERCENTAGE && value > 100) {
      value = 100
    }

    setFormState(
      {
        entitlement: {
          ...pricingEntitlement,
          concessionAmount: value
        }
      },
      [DiscountFieldPaths.CONCESSION_AMOUNT]
    )
  }

  const handleTypeChange = (value: string) => {
    const newType = value as AmountType

    const newMaxAmount = newType === AmountType.FLAT ? undefined : maximumConcessionAmount

    let newConcessionAmount = concessionAmount
    if (newType === AmountType.PERCENTAGE && concessionAmount > 100) {
      newConcessionAmount = 100
    }

    setFormState({
      entitlement: {
        ...pricingEntitlement,
        amountType: newType,
        maximumConcessionAmount: newMaxAmount,
        concessionAmount: newConcessionAmount
      }
    })

    if (newType === AmountType.FLAT) {
      setIsMaxAmountInputOpen(false)
    }
  }

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setFormState(
        {
          entitlement: {
            ...pricingEntitlement,
            maximumConcessionAmount: undefined
          }
        },
        [DiscountFieldPaths.MAXIMUM_CONCESSION_AMOUNT]
      )
      return
    }
    let value = parseFloat(e.target.value)

    if (value > 999999) {
      value = 999999
    }
    setFormState(
      {
        entitlement: {
          ...pricingEntitlement,
          maximumConcessionAmount: value
        }
      },
      [DiscountFieldPaths.MAXIMUM_CONCESSION_AMOUNT]
    )
  }

  const handleMaxBlur = () => {
    if (!maximumConcessionAmount || maximumConcessionAmount === 0) {
      handleClearMaxAmount()
    }
  }

  const handleClearMaxAmount = () => {
    setIsMaxAmountInputOpen(false)
    setFormState(
      {
        entitlement: {
          ...pricingEntitlement,
          maximumConcessionAmount: undefined
        }
      },
      [DiscountFieldPaths.MAXIMUM_CONCESSION_AMOUNT]
    )
  }

  const isUptoVisible = showUpto && amountType === AmountType.PERCENTAGE && isMaxAmountInputOpen
  return (
    <div className='flex flex-col'>
      <div className='flex w-full '>
        <Input
          id='discount-main-amount-input'
          type='number'
          value={concessionAmount || ''}
          placeholder='e.g. 25'
          onChange={handleMainAmountChange}
          dimension='md'
          startAdornment={
            <div className='flex items-center h-full'>
              <Select value={amountType} onValueChange={handleTypeChange}>
                <SelectTrigger
                  id='discount-amount-type-trigger'
                  showCaret={false}
                  className='ml-[-5px] h-full gap-1.5 rounded-md px-1 py-1 border-1 border-zinc-950/10 bg-white hover:bg-zinc-500/10 shadow-sm focus:ring-0 focus:border-zinc-950/10'
                >
                  <div className='flex items-center gap-1.5 pr-1.5 text-zinc-500'>
                    <Icon
                      IconComponent={amountType === AmountType.PERCENTAGE ? Percent : CurrencyInr}
                      size='sm'
                      className='text-zinc-500'
                    />
                    <Icon
                      IconComponent={CaretDown}
                      size='xxxs'
                      iconProps={{ weight: 'fill' }}
                      className='text-zinc-500'
                    />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem id='amount-type-percentage' value={AmountType.PERCENTAGE}>
                    <div className='flex items-center gap-2'>
                      <Icon IconComponent={Percent} size='sm' className='text-zinc-500' />
                      <span className='text-zinc-700 text-text-sm-regular'>{t('discounts_v2.percentage_label')}</span>
                    </div>
                  </SelectItem>
                  <SelectItem id='amount-type-flat' value={AmountType.FLAT}>
                    <div className='flex items-center gap-2'>
                      <Icon IconComponent={CurrencyInr} size='sm' className='text-zinc-500' />
                      <span className='text-zinc-700 text-text-sm-regular'>{t('discounts_v2.fixed_amount_label')}</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          }
          endAdornment={
            <div className='flex items-center gap-2 pl-2'>
              <span className='text-text-sm-regular text-zinc-400'>{t('discounts_v2.off_label')}</span>
            </div>
          }
          className={classNames(
            'text-zinc-800 !justify-between flex-1 relative z-[1] focus-within:z-[2]',
            showUpto && amountType === AmountType.PERCENTAGE ? 'rounded-r-none' : ''
          )}
          error={!!error}
        />

        {showUpto && amountType === AmountType.PERCENTAGE && !isUptoVisible && (
          <div
            id='upto-button'
            onClick={() => setIsMaxAmountInputOpen(true)}
            className='flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-r-[8px] rounded-l-none bg-white px-2 py-1 text-text-sm-regular text-zinc-400 input-outline hover:input-outline-hover active:input-outline-active focus-within:input-outline-focus transition-all relative z-[1]'
          >
            {t('discounts_v2.upto_question')}
          </div>
        )}

        {isUptoVisible && (
          <Input
            id='upto-amount-input'
            type='number'
            value={maximumConcessionAmount || ''}
            onChange={handleMaxChange}
            onBlur={handleMaxBlur}
            dimension='md'
            className='w-[50%] rounded-l-none text-zinc-800 ml-0 relative z-[1] focus-within:z-[2]'
            startAdornment={
              <div className='flex items-center gap-1'>
                <span className='text-text-sm-regular text-zinc-400'>{t('discounts_v2.upto_label').toLowerCase()}</span>
                <Icon IconComponent={CurrencyInr} size='xs' className='text-zinc-400' />
              </div>
            }
            max={999999}
            endAdornment={
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    id='clear-upto-button'
                    IconComponent={ArrowUUpLeft}
                    variant='ghost'
                    size='xs'
                    className='mr-1 cursor-pointer text-zinc-400 hover:text-zinc-600'
                    onClick={handleClearMaxAmount}
                  />
                </TooltipTrigger>
                <TooltipContent>Reset</TooltipContent>
              </Tooltip>
            }
          />
        )}
      </div>
      <FormErrorText message={error}/>
    </div>
  )
}

export default DiscountPricingInput
